/**
 * The landing tick (lane A's gun-pit profile, 11–21 ms): a crater's passes — its dig (the JS crater, the mound), its
 * rim, its ground look, its scorch — each ran over every vertex of the planet (61,440 on the Lab's) for a disc of a few
 * hundred, and, run once a landing, never warm: every double in their loops was boxed, ~5 MB of garbage a pass. A
 * planet mesh's vertices filed once by cube-sphere cell (core/cells.ts; its base positions never move, so one index per
 * array), and a pass visits only the vertices within its reach — in ascending order, the whole-planet scan's, each with
 * the scan's own arithmetic: the same results, bit for bit (tests/vertexCells.test.ts against the scan).
 */
import type { Vec3 } from '../types';
import { buildCellIndex, queryCellIndex, type CellIndex } from './cells';
import { dacos, dcos } from './dmath';

/** The vertices a cell holds on average, which sets the index's cells per edge. */
const PER_CELL = 24;
const indexes = new WeakMap<Float32Array, CellIndex>();
let off = false;

function indexOf(bp: Float32Array): CellIndex {
  let ix = indexes.get(bp);
  if (!ix) {
    const count = Math.floor(bp.length / 3);
    const n = Math.max(1, Math.min(128, Math.round(Math.sqrt(count / (6 * PER_CELL)))));
    const d = { x: 0, y: 0, z: 0 };   // (cellOf reads a direction's face and ratios: a base position needs no normalising)
    ix = buildCellIndex(count, i => { d.x = bp[i * 3]; d.y = bp[i * 3 + 1]; d.z = bp[i * 3 + 2]; return d; }, n);
    indexes.set(bp, ix);
  }
  return ix;
}

/**
 * The vertices of a mesh (`bp`, its base positions) whose direction lies within `angle` radians of the unit direction
 * `dir` — every one, among others near — ascending; null when the pass should scan them all (a reach of an eighth of a
 * turn or more, or the index off for a test). The reach is widened by 1e-8 in its cosine, past any pass's own margin
 * (a rim's or a scorch's cosine is a hair under its reach's).
 */
export function verticesNear(bp: Float32Array, dir: Vec3, angle: number): number[] | null {
  if (off || !(angle < Math.PI / 4)) return null;
  return queryCellIndex(indexOf(bp), dir, dacos(Math.max(-1, dcos(angle) - 1e-8)));
}

/**
 * Build a mesh's index (and its cells' table) now, where a hitch doesn't show — match setup — not on the first crater's
 * tick (2.6 ms for the Lab's 61,440 vertices, and the table's 1.8, on top of that landing). A cache: nothing changes.
 */
export function prepareVertexCells(bp: Float32Array): void {
  queryCellIndex(indexOf(bp), { x: 0, y: 1, z: 0 }, 1e-9);   // (a query builds the cells' table for the index's n)
}

/** Run `fn` with the index off: every pass over the whole planet, as before it (the equality tests). */
export function withoutVertexCells<T>(fn: () => T): T {
  const was = off;
  off = true;
  try { return fn(); } finally { off = was; }
}
